<template>
  <div
    class="mb-3 rounded-lg border border-solid border-neutralLight bg-[#F6F6F9] px-3 py-2 text-left text-sm text-textPrimary"
    data-testid="unidentified-mirror-notice"
  >
    <p>
      <b>{{ headline }}</b> {{ explanation }}
      <button
        v-if="stations.length"
        type="button"
        class="ml-1 font-bold text-primary30 underline"
        data-testid="unidentified-mirror-notice-toggle"
        @click="expanded = !expanded"
      >
        {{ expanded ? 'Ocultar' : toggleLabel }}
      </button>
    </p>
    <ul v-if="expanded" class="mt-2 flex flex-col gap-0.5 text-xs text-[#68688D]">
      <li
        v-for="station in stations"
        :key="station.doctorStationId"
        data-testid="unidentified-mirror-notice-item"
      >
        {{ describeStation(station) }}
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { format } from 'date-fns'
import type { DSAttendanceUnidentifiedCoverageStation } from '@/Network/Types/Responses/DsAttendancePolicyResponses'
import { formatTimeCell } from '../utils/prefill-mirror'

/**
 * M13-A — aviso acima do espelho da folha: plantões ocupados sem identificação ficam FORA do
 * universo esperado e não são candidatos da conciliação (Q22). O front só avisa; nada muda
 * no espelho nem na conciliação.
 */

const props = defineProps<{ count: number; stations: DSAttendanceUnidentifiedCoverageStation[] }>()

const expanded = ref(false)
const isSingle = computed(() => props.count === 1)
const headline = computed(() =>
  isSingle.value
    ? '+1 plantão ocupado sem identificação nesta escala e período.'
    : `+${props.count} plantões ocupados sem identificação nesta escala e período.`
)
const explanation = computed(() =>
  isSingle.value
    ? 'Ele não entra no espelho nem na conciliação, porque não há médico a conferir.'
    : 'Eles não entram no espelho nem na conciliação, porque não há médico a conferir.'
)
const toggleLabel = computed(() =>
  isSingle.value ? 'Ver o plantão' : `Ver os ${props.count} plantões`
)

function describeStation(station: DSAttendanceUnidentifiedCoverageStation): string {
  const day = format(new Date(station.startHour), 'dd/MM')
  const range = `${formatTimeCell(station.startHour)}–${formatTimeCell(station.endHour)}`
  return [`${day} ${range}`, station.coverageResponsibleName].filter(Boolean).join(' · ')
}
</script>
